import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, PieChart, Pie, Cell, Tooltip, LineChart, Line, CartesianGrid } from "recharts";
import { Users, Clock, CheckCircle2, XCircle, TrendingUp, IndianRupee } from "lucide-react";

const COLORS = ["#39FF14", "#FFD700", "#00E0FF", "#FF2442", "#B84EFF", "#FF9500", "#00FF9B", "#FFFFFF"];

export default function AdminDashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-white/50">Loading…</div>;
  const roleData = Object.entries(s.by_role).map(([name, value]) => ({ name, value }));
  const tehsilData = Object.entries(s.by_tehsil).map(([name, value]) => ({ name, value })).sort((a,b)=>b.value-a.value).slice(0,10);
  const dailyData = Object.entries(s.daily).map(([day, count]) => ({ day: day.slice(5), count }));

  const Stat = ({ i: Icon, l, v, c }) => (
    <div className="p-5 bg-[#0B132B] border border-white/10 rounded-lg">
      <div className={`w-10 h-10 rounded grid place-items-center ${c} mb-3`}><Icon size={18}/></div>
      <div className="font-display text-4xl">{v}</div>
      <div className="text-xs uppercase tracking-widest text-white/60 mt-1">{l}</div>
    </div>
  );
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.4em] text-[#39FF14]">Overview</div>
      <h1 className="font-display text-4xl mt-2">Dashboard</h1>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="stats-grid">
        <Stat i={Users} l="Total" v={s.total} c="bg-[#39FF14] text-black" />
        <Stat i={Clock} l="Pending" v={s.pending} c="bg-[#FFD700] text-black" />
        <Stat i={CheckCircle2} l="Approved" v={s.approved} c="bg-[#00E0FF] text-black" />
        <Stat i={XCircle} l="Rejected" v={s.rejected} c="bg-[#FF2442] text-white" />
        <Stat i={IndianRupee} l="Unpaid" v={s.unpaid} c="bg-white text-black" />
        <Stat i={TrendingUp} l="Today" v={s.today} c="bg-[#B84EFF] text-white" />
      </div>
      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <div className="p-6 bg-[#0B132B] border border-white/10 rounded-lg">
          <div className="text-xs uppercase tracking-widest text-white/60 mb-4">Daily Registrations (last 14 days)</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={dailyData}>
              <CartesianGrid stroke="#1A2542" />
              <XAxis dataKey="day" stroke="#A0AAB2" tick={{fontSize: 11}} /><YAxis stroke="#A0AAB2" tick={{fontSize: 11}}/>
              <Tooltip contentStyle={{background:"#0B132B", border:"1px solid #39FF14"}} />
              <Line type="monotone" dataKey="count" stroke="#39FF14" strokeWidth={2} dot={{fill: "#FFD700"}} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="p-6 bg-[#0B132B] border border-white/10 rounded-lg">
          <div className="text-xs uppercase tracking-widest text-white/60 mb-4">Players by Role</div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={roleData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}>
                {roleData.map((r, i) => <Cell key={r.name} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{background:"#0B132B", border:"1px solid #39FF14"}} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            {roleData.map((r,i) => <div key={r.name} className="flex items-center gap-2"><div className="w-3 h-3" style={{background: COLORS[i%COLORS.length]}}/> {r.name} · {r.value}</div>)}
          </div>
        </div>
      </div>
      <div className="mt-6 p-6 bg-[#0B132B] border border-white/10 rounded-lg">
        <div className="text-xs uppercase tracking-widest text-white/60 mb-4">Top Tehsils</div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={tehsilData}>
            <CartesianGrid stroke="#1A2542"/>
            <XAxis dataKey="name" stroke="#A0AAB2" tick={{fontSize: 10}} angle={-20} textAnchor="end" height={60}/>
            <YAxis stroke="#A0AAB2" tick={{fontSize: 11}}/>
            <Tooltip contentStyle={{background:"#0B132B", border:"1px solid #39FF14"}} />
            <Bar dataKey="value" fill="#39FF14" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
